// Slim status pill for offline mode: shows when the device is offline (with
// the count of writes saved locally) and briefly while queued writes sync.
import { useEffect, useState } from "react";
import { CloudOff, RefreshCw } from "lucide-react";
import { useT } from "../lib/i18n";
import { queueCount } from "../lib/offlineQueue";

export function OfflineBanner() {
  const t = useT();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    void queueCount().then(setPending);
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    const onQueue = (e: Event) => setPending((e as CustomEvent<{ pending: number }>).detail.pending);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("caloly:queue-changed", onQueue);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("caloly:queue-changed", onQueue);
    };
  }, []);

  if (online && pending === 0) return null;

  return (
    <div className="mx-4 mt-2 flex items-center gap-2 rounded-xl bg-warn/15 px-3 py-2 text-sm">
      {online ? (
        <RefreshCw size={16} className="text-warn shrink-0 animate-spin" aria-hidden="true" />
      ) : (
        <CloudOff size={16} className="text-warn shrink-0" aria-hidden="true" />
      )}
      <span className="flex-1 text-fg-secondary">
        {online
          ? t("offline.syncing", { n: pending })
          : pending > 0
            ? t("offline.saved_locally", { n: pending })
            : t("offline.offline")}
      </span>
    </div>
  );
}
